import * as Sentry from '@sentry/browser'
import React, { useState, FormEvent, useEffect } from 'react'
import { flowRight as compose, isEmpty } from 'lodash'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import type { RouteChildrenProps } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'
import { loaderState } from 'recoils/atoms'

import {
  StockKeepingUnitForm,
  initialStockKeepingUnitForm,
  schemaStockKeepingUnitForm,
  formatForm,
  StockKeepingUnitService,
  BREADCRUMBS_CREATE,
} from 'features/StockKeepingUnit'
import { Button, PromptGuard, Modal } from 'components'
import { Confirmation, ModalSuccess } from 'components/Modal'
import { withBackOfficeCms } from 'helpers/hocs'
import { routes } from 'helpers/routes'
import {
  useBreadcrumbs,
  useFormSubmitLoader,
  usePromptForm,
} from 'helpers/hooks'

import type {
  IStockKeepingUnitForm,
  IStockKeepingUnitFormPayload,
  IFileList,
} from 'features/StockKeepingUnit'

function StockKeepingUnitCreateContainer({
  history,
}: RouteChildrenProps): JSX.Element {
  const { t } = useTranslation(['common', 'layout'])

  const base = 'con-stock-keeping-unit__form'
  const methods = useForm<IStockKeepingUnitForm>({
    defaultValues: initialStockKeepingUnitForm,
    resolver: yupResolver(schemaStockKeepingUnitForm),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })
  const [shouldPrompt, setShouldPrompt] =
    usePromptForm<IStockKeepingUnitForm>(methods)
  const [loading] = useFormSubmitLoader<IStockKeepingUnitForm>(methods)
  const [payload, setPayload] = useState<IStockKeepingUnitFormPayload>()
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false)
  const [isOpenSuccess, setIsOpenSuccess] = useState<boolean>(false)
  const [fileList, setFileList] = useState<IFileList[]>([])
  const setLoader = useSetRecoilState(loaderState)
  useBreadcrumbs(BREADCRUMBS_CREATE)

  const callApi = async () => {
    if (payload) {
      try {
        setLoader(true)
        const photos: Blob[] = []
        fileList.forEach((e) => {
          if (e.file) {
            photos.push(e.file)
          }
        })

        if (photos.length > 0) {
          payload.photos = photos
        }

        await StockKeepingUnitService.create(payload, handleOpenSuccess)
        setLoader(false)
      } catch (error) {
        Sentry.captureException(error)
        setLoader(false)
      }
    }
  }

  const setData = (data: IStockKeepingUnitForm) => {
    const payload = formatForm(data)
    if (isEmpty(methods.errors) && payload) {
      setIsOpenConfirm(true)
      setPayload(payload)
    }
  }

  const handleOpenSuccess = async () => {
    setIsOpenSuccess(true)

    await setTimeout(() => {
      setShouldPrompt(false)
      history.push(routes.product.path)
    }, 2000)
  }

  const beforeSubmit = (e: FormEvent<HTMLFormElement>) => {
    methods.setValue('currentState', 'onSubmit')
    methods.handleSubmit(setData)(e)
    methods.setValue('currentState', '')
  }

  const renderConfirmModal = () => {
    return (
      isOpenConfirm && (
        <Modal
          isIcon={false}
          isOpen={isOpenConfirm}
          onClose={() => setIsOpenConfirm(false)}
        >
          <Confirmation
            title={t('common:confirmation.title.label')}
            description={t('common:confirmation.description.label')}
            confirmText={t('common:save.button')}
            cancelText={t('common:cancel.button')}
            onConfirm={callApi}
            onClose={() => setIsOpenConfirm(false)}
          />
        </Modal>
      )
    )
  }

  const renderSuccessModal = () => {
    return (
      isOpenSuccess && (
        <ModalSuccess
          isOpen={isOpenSuccess}
          description={t('common:success.label')}
          onClose={() => setIsOpenSuccess(false)}
        />
      )
    )
  }

  useEffect(() => {
    if (isOpenSuccess) {
      handleOpenSuccess()
    }
  }, [isOpenSuccess])

  return (
    <FormProvider {...methods}>
      <PromptGuard when={shouldPrompt} />
      <form className={base} onSubmit={beforeSubmit}>
        <StockKeepingUnitForm
          isEdit={false}
          setFileList={setFileList}
          fileList={fileList}
        />
        <div className='container__form__actions'>
          <Button
            variants='secondary'
            onClick={() => history.push(routes.product.path)}
          >
            {t('common:cancel.button')}
          </Button>

          <Button type='submit' isLoading={loading}>
            {t('common:create.button')}
          </Button>
        </div>
      </form>
      {renderConfirmModal()}
      {renderSuccessModal()}
    </FormProvider>
  )
}

export default compose(withBackOfficeCms)(StockKeepingUnitCreateContainer)
